import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import styles from './BrokerLayout.module.css';
import { FiMenu, FiX } from 'react-icons/fi';
import { useLocationContext } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { useUserPermissions } from '../hooks/useUserPermissions';
import BrokerNotificationsTray from '../pages/broker/BrokerNotificationsTray';
import { useEnhancedBrokerNotifications } from '../hooks/useEnhancedBrokerNotifications';
import { useSidebarRouteAck } from '../utils/brokerShipperSidebarRouteAck';
import { NotificationBellIcon, NotificationBellBadge } from '../components/notifications/NotificationBellIcon';
import { MigrationDataAccessLayer } from '../services/migrationDataAccessLayer';
import { onboardingService } from '../services/onboardingService';
import Logger from '../services/logger';
import { usePortalShellGuard } from '../hooks/usePortalShellGuard';
import { resolvePortalProductUserType } from '../utils/portalAccessUtils';
import { APPROVAL_STATUS, APPROVAL_STATUS_LOGIN_MESSAGE } from '../constants/registrationState';
import { signOut } from 'firebase/auth';
import { auth } from '../config/firebase';

const BrokerLayout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [companyName, setCompanyName] = useState<string>('Broker Portal');
  const [onboardingStatus, setOnboardingStatus] = useState<{
    onboardingCompleted: boolean | null;
    checking: boolean;
  }>({ onboardingCompleted: null, checking: true });
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { location: userLocation, error: locationError, permissionState, retry } = useLocationContext();

  const allowedRoutes = ['/broker/onboarding'];

  usePortalShellGuard('broker', allowedRoutes);

  // Initialize migration data access layer
  const migrationDataAccess = new MigrationDataAccessLayer();

  // Block pending/rejected brokers from portal shell (team invites may complete onboarding while pending)
  useEffect(() => {
    const guardApproval = async () => {
      if (!user?.uid) return;
      try {
        const result = await migrationDataAccess.getUserProfile(user.uid);
        if (!result.success || !result.data) return;
        const userData = result.data;
        if (resolvePortalProductUserType(userData as Record<string, unknown>) !== 'broker') return;

        const approvalStatus = String(userData.approvalStatus || APPROVAL_STATUS.PENDING).toLowerCase();
        if (approvalStatus === APPROVAL_STATUS.APPROVED) return;

        const isTeamInviteMember = userData.joinedViaTeamInvite === true;
        const onOnboardingRoute = allowedRoutes.some((route) => location.pathname.startsWith(route));
        if (isTeamInviteMember && onOnboardingRoute) return;

        const message =
          APPROVAL_STATUS_LOGIN_MESSAGE[
            approvalStatus === APPROVAL_STATUS.REJECTED
              ? APPROVAL_STATUS.REJECTED
              : APPROVAL_STATUS.PENDING
          ];
        try {
          await signOut(auth);
        } catch {
          /* best-effort */
        }
        navigate('/login', { replace: true, state: { registrationMessage: message } });
      } catch (err) {
        Logger.error('[BrokerLayout] approval guard failed', err instanceof Error ? err : new Error(String(err)));
      }
    };
    guardApproval();
  }, [user?.uid, navigate, location.pathname]);


  // Use our enhanced notification counting hook
  const { counts, markPageVisited } = useEnhancedBrokerNotifications();
  useSidebarRouteAck(markPageVisited, location.pathname);

  // Check onboarding status and redirect if not completed (same pattern as CarrierLayout)
  useEffect(() => {
    const checkOnboarding = async () => {
      if (!user?.uid) {
        setOnboardingStatus({ onboardingCompleted: null, checking: false });
        return;
      }
      try {
        setOnboardingStatus((prev) => ({ ...prev, checking: true }));
        const result = await migrationDataAccess.getUserProfile(user.uid);
        if (!result.success || !result.data) {
          setOnboardingStatus({ onboardingCompleted: null, checking: false });
          return;
        }
        const userData = result.data;
        if (resolvePortalProductUserType(userData as Record<string, unknown>) !== 'broker') {
          setOnboardingStatus({ onboardingCompleted: null, checking: false });
          return;
        }
        const isAllowedRoute = allowedRoutes.some((route) => location.pathname.startsWith(route));
        if (isAllowedRoute) {
          setOnboardingStatus({ onboardingCompleted: null, checking: false });
          return;
        }
        const onboardingCompleted = await onboardingService.checkOnboardingStatus(user.uid);
        setOnboardingStatus({ onboardingCompleted, checking: false });
        if (!onboardingCompleted) {
          navigate('/broker/onboarding', { replace: true });
        }
      } catch (err) {
        Logger.error('[BrokerLayout] Error checking onboarding:', err instanceof Error ? err : new Error(String(err)), { err });
        setOnboardingStatus({ onboardingCompleted: null, checking: false });
      }
    };
    checkOnboarding();
  }, [user?.uid, navigate, location.pathname]);

  // Re-apply viewport on mount so mobile browser recalculates after client-side nav from login (fixes "must shrink then refresh" glitch)
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (meta) {
      const content = 'width=device-width, initial-scale=1';
      meta.setAttribute('content', content);
      requestAnimationFrame(() => {
        window.visualViewport?.dispatchEvent(new Event('resize'));
      });
    }
  }, []);

  // Fetch company name for display
  useEffect(() => {
    const fetchCompanyName = async () => {
      if (!user?.uid) return;
      
      try {
        // Use dual-mode access to get user profile
        const result = await migrationDataAccess.getUserProfile(user.uid);
        if (result.success && result.data) {
          const userData = result.data;
          if (userData.companyName) {
            setCompanyName(userData.companyName);
          }
        }
      } catch (error) {
        Logger.error('Error fetching company name:', error instanceof Error ? error : new Error(String(error)), { error });
      }
    };

    fetchCompanyName();
  }, [user?.uid]);


  const handleLogout = () => {
    navigate('/login');
  };

  return (
    <div className={styles.layout}>
      {/* Mobile menu button */}
      <button 
        className={styles.mobileMenuButton}
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        aria-label="Toggle menu"
      >
        {isSidebarOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24">
            <line x1="18" y1="6" x2="6" y2="18" stroke="white" strokeWidth="2"/>
            <line x1="6" y1="6" x2="18" y2="18" stroke="white" strokeWidth="2"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24">
            <line x1="3" y1="12" x2="21" y2="12" stroke="white" strokeWidth="2"/>
            <line x1="3" y1="6" x2="21" y2="6" stroke="white" strokeWidth="2"/>
            <line x1="3" y1="18" x2="21" y2="18" stroke="white" strokeWidth="2"/>
          </svg>
        )}
      </button>
      {/* Sidebar overlay for mobile */}
      {isSidebarOpen && <div className={styles.sidebarOverlay} onClick={() => setIsSidebarOpen(false)} />}
      <nav className={`${styles.sidebar} ${isSidebarOpen ? styles.sidebarOpen : ''}`}>
        <div className={styles.logo}>
          <h2>{companyName}</h2>
        </div>
        <NavLink 
          to="/broker/dashboard" 
          className={({ isActive }) => isActive ? styles.activeLink : styles.link}
          onClick={() => {
            setIsSidebarOpen(false);
            markPageVisited('dashboard');
          }}
        >
          Dashboard
        </NavLink>
        <NavLink 
          to="/broker/driver-updates" 
          className={({ isActive }) => isActive ? styles.activeLink : styles.link}
          onClick={() => {
            setIsSidebarOpen(false);
            markPageVisited('driverUpdates');
          }}
        >
          {counts.driverUpdates > 0 && <span className={styles.notificationDot}></span>}
          Driver Updates
        </NavLink>
        <NavLink 
          to="/broker/shipping-schedule" 
          className={({ isActive }) => isActive ? styles.activeLink : styles.link}
          onClick={() => {
            setIsSidebarOpen(false);
            markPageVisited('shippingSchedule');
          }}
        >
          {counts.shippingSchedule > 0 && <span className={styles.notificationDot}></span>}
          Shipping Schedule
        </NavLink>
        <NavLink 
          to="/broker/purchase-orders" 
          className={({ isActive }) => isActive ? styles.activeLink : styles.link}
          onClick={() => {
            setIsSidebarOpen(false);
            markPageVisited('purchaseOrders');
          }}
        >
          {counts.purchaseOrders > 0 && <span className={styles.notificationDot}></span>}
          Purchase Orders
        </NavLink>
        <NavLink 
          to="/broker/carrier-partners" 
          className={({ isActive }) => isActive ? styles.activeLink : styles.link}
          onClick={() => {
            setIsSidebarOpen(false);
            markPageVisited('carrierPartners');
          }}
        >
          {counts.carrierPartners > 0 && <span className={styles.notificationDot}></span>}
          Carrier Partners
        </NavLink>
        <NavLink 
          to="/broker/pay-invoices" 
          className={({ isActive }) => isActive ? styles.activeLink : styles.link}
          onClick={() => {
            setIsSidebarOpen(false);
            markPageVisited('payments');
          }}
        >
          {counts.payments > 0 && <span className={styles.notificationDot}></span>}
          Pay Invoices
        </NavLink>
        <NavLink 
          to="/broker/shipment-archive" 
          className={({ isActive }) => isActive ? styles.activeLink : styles.link}
          onClick={() => {
            setIsSidebarOpen(false);
            markPageVisited('shipmentArchive');
          }}
        >
          {counts.shipmentArchive > 0 && <span className={styles.notificationDot}></span>}
          Shipment Archive
        </NavLink>
        <NavLink 
          to="/broker/carrier-directory" 
          className={({ isActive }) => isActive ? styles.activeLink : styles.link}
          onClick={() => setIsSidebarOpen(false)}
        >
          Carrier Directory
        </NavLink>
        <NavLink 
          to="/broker/settings" 
          className={({ isActive }) => isActive ? styles.activeLink : styles.link}
          onClick={() => setIsSidebarOpen(false)}
        >
          Settings
        </NavLink>
      </nav>
      <main className={styles.main}>
        <header className={styles.header}>
          <h1>Broker Dashboard</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative' }}>
            <button 
                className={styles.bellButton}
                onClick={() => setShowNotifications(v => !v)}
                type="button"
                aria-label="Notifications"
              >
                <NotificationBellIcon size={28} hasUnread={counts.bellOnly > 0} />
                <NotificationBellBadge count={counts.bellOnly} />
              </button>
            {showNotifications && (
              <BrokerNotificationsTray onClose={() => setShowNotifications(false)} />
            )}
            <div className={styles.menuContainer}>
              <button 
                className={styles.hamburgerButton}
                onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
              >
                <div className={styles.hamburgerIcon}>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </button>
              {isAccountMenuOpen && (
                <div className={styles.dropdownMenu}>
                  <button onClick={() => navigate('/broker/profile')}>Account</button>
                  <button 
                    onClick={handleLogout}
                    className={styles.logoutButton}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        <div className={styles.pageContent}>
          {onboardingStatus.checking && !allowedRoutes.some((r) => location.pathname.startsWith(r)) && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 100,
                flexDirection: 'column',
                gap: '1rem',
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #007bff',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
              <p style={{ color: '#666' }}>Checking access...</p>
              <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
          )}
          <div className={styles.pageContentInner}>
            <Outlet />
          </div>
        </div>
      </main>
      {/* Location authentication modal - show for all users when location is not available */}
      {!location && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.45)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 32,
            maxWidth: 400,
            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            textAlign: 'center',
          }}>
            <h2>Location Required</h2>
            <p style={{ marginBottom: 16 }}>
              This app requires your location to function. Please allow location access when prompted.<br/>
              {locationError && <span style={{ color: '#b00' }}>{locationError}</span>}
            </p>
            <button onClick={retry} style={{
              background: '#007bff',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '10px 24px',
              fontSize: 16,
              cursor: 'pointer',
            }}>Retry</button>
            <div style={{ marginTop: 16, fontSize: 13, color: '#666' }}>
              If you previously denied location, please enable it in your browser settings and click Retry.
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default BrokerLayout;
